import { useState, useEffect, useCallback, useRef } from 'react';
import { usePersistedState } from '@/hooks/use-persisted-state';
import { StorageKeys, StorageSchemas } from '@/lib/storage';
import { fetchWithAuth } from '@/lib/api';

interface GraphNode {
  id: string;
  title: string;
  type: 'work-spec' | 'note' | 'voice-memo' | 'basecamp-todo' | string;
  projectName?: string;
  /** When present on API payloads, used for project filter UI */
  projectId?: string;
  filePath: string;
  color: string;
  shape: string;
}

interface GraphLink {
  source: string;
  target: string;
  type: 'manual' | 'ai' | 'flow-job';
  similarity?: number;
  linkText?: string;
  flowJobName?: string;
  color: string;
  strokeDasharray: string;
  width: number;
}

interface GraphData {
  nodes: GraphNode[];
  links: GraphLink[];
  stats?: {
    nodeCount: number;
    linkCount: number;
    manualLinks: number;
    aiConnections: number;
    flowJobConnections: number;
  };
}

export function useGraphState() {
  const [graphData, setGraphData] = useState<GraphData>({ nodes: [], links: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [similarityThreshold, setSimilarityThreshold] = usePersistedState({
    key: StorageKeys.GRAPH_SIMILARITY,
    defaultValue: 0.3,
    schema: StorageSchemas.number,
  });
  const [showManualLinks, setShowManualLinks] = usePersistedState({
    key: StorageKeys.GRAPH_MANUAL_LINKS,
    defaultValue: true,
    schema: StorageSchemas.boolean,
  });
  const [showAIConnections, setShowAIConnections] = usePersistedState({
    key: StorageKeys.GRAPH_AI_CONNECTIONS,
    defaultValue: true,
    schema: StorageSchemas.boolean,
  });
  const [showFlowJobs, setShowFlowJobs] = usePersistedState({
    key: StorageKeys.GRAPH_FLOW_JOBS,
    defaultValue: true,
    schema: StorageSchemas.boolean,
  });
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [groupBy, setGroupBy] = usePersistedState<'none' | 'folder' | 'project' | 'client'>({
    key: StorageKeys.GRAPH_GROUP_BY,
    defaultValue: 'none',
    schema: StorageSchemas.graphGroupBy,
  });
  const [filterProject, setFilterProject] = usePersistedState<string | null>({
    key: StorageKeys.GRAPH_FILTER_PROJECT,
    defaultValue: null,
  });
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fetchGraphDataRef = useRef<(() => Promise<void>) | null>(null);

  const fetchGraphData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        similarityThreshold: similarityThreshold.toString(),
        includeManualLinks: showManualLinks.toString(),
        includeAIConnections: showAIConnections.toString(),
        includeFlowJobs: showFlowJobs.toString(),
        limit: '1000', // Ensure we get enough nodes
      });

      console.log('[Graph State] Fetching with threshold:', similarityThreshold);
      const response = await fetchWithAuth(`/api/graph/all?${params}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch graph data: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to fetch graph data');
      }

      console.log('[Graph State] Received data:', {
        nodes: data.nodes?.length || 0,
        links: data.links?.length || 0,
        threshold: similarityThreshold
      });
      
      setGraphData({
        nodes: data.nodes || [],
        links: data.links || [],
        stats: data.stats
      });
    } catch (err) {
      console.error('Failed to fetch graph data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load graph');
    } finally {
      setLoading(false);
    }
  }, [similarityThreshold, showManualLinks, showAIConnections, showFlowJobs]);
  
  // Keep ref updated with latest fetchGraphData
  useEffect(() => {
    fetchGraphDataRef.current = fetchGraphData;
  }, [fetchGraphData]);

  // Track if initial mount has completed
  const hasMountedRef = useRef(false);
  
  // Debounce threshold/link toggle changes to avoid excessive API calls while dragging slider
  useEffect(() => {
    // Skip on initial mount (handled by separate effect)
    if (!hasMountedRef.current) {
      return;
    }
    
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      console.log('[Graph State] Debounced fetch triggered, threshold:', similarityThreshold);
      if (fetchGraphDataRef.current) {
        fetchGraphDataRef.current(); // Use ref to get latest function
      }
    }, 300); // 300ms debounce
    
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [similarityThreshold, showManualLinks, showAIConnections, showFlowJobs]); // Don't include fetchGraphData to avoid resetting timer
  
  // Initial fetch on mount only
  useEffect(() => {
    console.log('[Graph State] Initial fetch');
    fetchGraphData();
    hasMountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount
  
  // Initial fetch on mount only
  useEffect(() => {
    console.log('[Graph State] Initial fetch');
    fetchGraphData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  return {
    graphData,
    loading,
    error,
    similarityThreshold,
    setSimilarityThreshold,
    showManualLinks,
    setShowManualLinks,
    showAIConnections,
    setShowAIConnections,
    showFlowJobs,
    setShowFlowJobs,
    selectedNode,
    setSelectedNode,
    fetchGraphData,
    groupBy,
    setGroupBy,
    filterProject,
    setFilterProject
  };
}

